import {
  IconeBalanca,
  IconeChamado,
  IconeCheque,
  IconeIndicador,
  IconeOrdemServico,
  IconeRelatorio,
  IconeRepositorio,
} from '../components/Icones'
import { TERMOS, TEXTOS } from './glossario'
import type { GrupoDeNavegacao } from './navegacao'

/**
 * O grupo de fiscalização. Importe sempre por `./navegacao`.
 *
 * A divisão em arquivos e a procedência dos textos estão explicadas em `navegacaoMudancas.ts`;
 * administração, análise por IA e conta moram em `navegacaoAdministracao.ts`. Aqui vale um cuidado
 * a mais: quase tudo já existe no sistema anterior, e o `noSistemaAnterior` só aponta para o que de
 * fato funciona lá — item que existe mas está vazio, ou que hoje é planilha manual, fica sem link.
 *
 * **A ordem é a do mês do fiscal**, do pedido ao documento: a ordem de serviço abre o mês, os
 * chamados e os commits são o que aconteceu, o fechamento transforma isso em número, os
 * indicadores e os pontos administrativos são esse número olhado ao longo do tempo, e os
 * relatórios são o papel que sai no fim. Rótulo de item é a MESMA string do título da tela (Regra
 * nº 4): para quem é leigo, "Relatórios" no menu e "Relatórios do contrato" no título são duas telas.
 */

/** Onde o sistema anterior continua no ar. Os caminhos saem do `routes/web.php` dele. */
const ANTERIOR = 'https://sistemagestao.niteroi.rj.gov.br'

/**
 * Grupo "Acompanhamento do contrato": o que aconteceu no mês — a ordem que o abre, os chamados e os
 * commits.
 *
 * Era um grupo só com o de apuração ("Contrato e fiscalização", sete itens), e o menu lia como uma
 * lista sem fim. A ordem do mês do fiscal continua a mesma, item por item; o que mudou é que agora
 * ela tem um degrau no meio — antes do número, o que aconteceu; depois, o número e o papel.
 */
export const CONTRATO: GrupoDeNavegacao = {
  id: 'contrato',
  rotulo: 'Acompanhamento do contrato',
  itens: [
    {
      id: 'ordens-servico',
      rotulo: TEXTOS.ordensDeServico.titulo,
      para: '/ordens-servico',
      icone: IconeOrdemServico,
      // SEM `organizacao`, e é a mesma razão da rota: a ordem obriga a contratada, e quem é
      // obrigado precisa vê-la. Emitir e revogar continua sendo ato da Prefeitura, decidido pelo
      // servidor. É a mesma escolha já feita em `/cab/calendario`.
      situacao: 'pronto',
      resumo: 'As ordens de serviço abertas para a contratada e o que já foi entregue em cada uma.',
      noSistemaAnterior: `${ANTERIOR}/os`,
    },
    {
      // Sem `noSistemaAnterior` desde que a tela existe aqui: a consulta de lá cobre outro recorte
      // de contrato (a exclusão fixa de SMF, PGM e SMA, que caducou com o SSTD 01/2026) e mandar o
      // fiscal comparar as duas listas produziria duas contagens do mesmo mês.
      id: 'chamados',
      rotulo: TERMOS.chamado.rotuloPlural,
      para: '/chamados',
      icone: IconeChamado,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: 'Os chamados do e-Niterói, que são a base da fiscalização do contrato.',
    },
    {
      // Nasceu do Diário de Bordo: dezenas de commits por semana no e-Cidade, da contratada, da
      // Fazenda e de contas pessoais, que nenhuma tela de fiscalização enxergava. Ao lado dos
      // chamados porque é a outra metade de "o que aconteceu no mês".
      id: 'repositorios',
      rotulo: TERMOS.atividadeNosRepositorios.rotulo,
      para: '/repositorios',
      icone: IconeRepositorio,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: 'Quem mexeu no código do e-Cidade neste mês, por setor e por pessoa, e o que merece conferência.',
    },
  ],
}

/** Grupo "Fechamento e medição": o mês virado em número — indicadores, pontos e os documentos. */
export const APURACAO: GrupoDeNavegacao = {
  id: 'apuracao',
  rotulo: 'Fechamento e medição',
  itens: [
    {
      // Item próprio, e não uma aba da lista de chamados: são duas perguntas diferentes. A lista
      // responde "o que aconteceu"; esta responde "quanto isso vale na nota do mês". Quem assina o
      // termo de recebimento vem direto para cá, e o endereço tem de ser dele.
      id: 'fiscalizacao',
      rotulo: TERMOS.fechamentoDoMes.rotulo,
      para: '/fiscalizacao',
      icone: IconeCheque,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: TERMOS.fechamentoDoMes.ajuda,
    },
    {
      // "IMR" é sigla do contrato e não diz nada a quem não é da fiscalização: a dica do item e o
      // "?" da tela trazem o nome por extenso e o que ele mede.
      id: 'indicadores',
      rotulo: TEXTOS.indicadores.titulo,
      para: '/indicadores',
      icone: IconeIndicador,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: TERMOS.imr.ajuda,
      noSistemaAnterior: `${ANTERIOR}/admin/imr`,
    },
    {
      // Sem `noSistemaAnterior`, e continua sem: lá esta apuração era planilha manual da
      // fiscalização e não existe tela nenhuma para apontar. Aqui ela virou registro — com a
      // pontuação saindo da tabela do contrato, e não da memória de quem apura.
      id: 'iad',
      rotulo: `${TERMOS.iad.rotulo} (${TERMOS.iad.sigla})`,
      para: '/iad',
      icone: IconeBalanca,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: TERMOS.iad.ajuda,
    },
    {
      id: 'relatorios',
      rotulo: TEXTOS.relatorios.titulo,
      para: '/relatorios',
      icone: IconeRelatorio,
      organizacao: 'CONTRATANTE',
      situacao: 'pronto',
      resumo: 'Os documentos do contrato. O relatório de uma solicitação já sai da tela dela própria.',
      noSistemaAnterior: `${ANTERIOR}/relatorio/chamado`,
    },
  ],
}
